//#region IMPORT
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { CommonActions } from '../../../../Reducer/CommonReducer';
import M_Master_BASIC from './M_Master_BASIC';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import Commons from '../../../../Services/Common/Common';
import {
  useSave1,
  useSearch1,
  useSearch2,
  useSearch3,
  useDelete1,
} from './M_Master_BASIC_Query';
import { NavBarInfoActions } from '../../../../Reducer/NavBarInfoReducer';
import { useBackStack } from '../../../../Hooks/back/useBackStack';
import { BottomSheetActions } from '../../../../Reducer/BottomSheetReducer';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';
import { TbmActions } from '../../../../Reducer/TbmReducer';
import { useSave } from '../../../../Hooks/useSave';
import { useDelete } from '../../../../Hooks/useDelete';
//#endregion

function M_Master_BASICF() {
  //#region 변수
  const { userInfo } = useSelector((state) => state.BaseInfo);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const activeStep = useSelector((state) => state.common.activeStep);
  const bottomSheetState = useSelector((state) => state.bottomSheet);
  const { sessionData, participantData, stepData, qrModal, meetFlag } =
    useSelector((state) => state.tbm);

  const [modalToggle3, setModalToggle3] = useState(false);
  const [modalToggle4, setModalToggle4] = useState(false);
  const [messages, setMessages] = useState([]);

  const ws = useRef(null);
  const heartbeatRef = useRef(null);
  const longTaskObserverRef = useRef(null);
  const frameWatchdogRafIdRef = useRef(null);
  const lastFrameTsRef = useRef(null);
  const activeStepRef = useRef(activeStep); // 최신 단계를 받아오기 위해 useRef로 선언(이전/다음 단계 구분용)

  const IsHost = participantData.hostYn === 'Y';
  activeStepRef.current = activeStep;
  //#endregion

  //#region 커스텀훅
  // 스택형 뒤로가기로 변경: 0단계(첫 진입)에서는 이탈 확인 바텀시트, qr모달 열려있을 때는 뒤로가기 차단
  useBackStack({
    type: 'sheet',
    enabled: IsHost && activeStep === 0 && !bottomSheetState.bottom,
    onBack: () =>
      dispatch(BottomSheetActions.OpenBottomSheet({ type: 'backClickTbm' })),
  });
  useBackStack({
    type: 'dialog',
    enabled: IsHost && qrModal && sessionData.sessionStatus === 'READY',
    onBack: () =>
      dispatch(
        showSnackbar({
          message: 'TBM 시작 준비가 완료되었습니다. 먼저 TBM을 시작해 주세요.',
          type: 'warning',
        })
      ),
  });
  useBackStack({
    type: 'dialog',
    enabled: IsHost && qrModal && sessionData.sessionStatus !== 'READY',
    onBack: () => dispatch(dispatch(TbmActions.setQrModal(false))),
  });
  //#endregion

  //#region 이벤트
  const StopHeartbeat = () => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  };

  const StartHeartbeat = () => {
    StopHeartbeat();
    heartbeatRef.current = setInterval(() => {
      if (ws.current && ws.current.readyState === WebSocket.OPEN) {
        ws.current.send(JSON.stringify({ type: 'ping' }));
        console.log('[tbm][WS] heartbeat ping 전송');
      } else {
        const state = ws.current ? ws.current.readyState : 'null';
        console.log(`[tbm][WS] heartbeat 전송 실패 — readyState=${state}`);
      }
    }, 30000);
  };

  /** rAF 간격이 벌어지면 메인 스레드가 막힌 것으로 간주 (iOS WKWebView에서도 동작) */
  const StopFrameWatchdog = () => {
    if (frameWatchdogRafIdRef.current != null) {
      cancelAnimationFrame(frameWatchdogRafIdRef.current);
      frameWatchdogRafIdRef.current = null;
    }
    lastFrameTsRef.current = null;
  };

  const StartFrameWatchdog = () => {
    StopFrameWatchdog();
    const JANK_MS = 100;
    const MAX_GAP_MS = 12000;

    const tick = () => {
      const now = performance.now();
      if (
        lastFrameTsRef.current != null &&
        typeof document !== 'undefined' &&
        !document.hidden
      ) {
        const delta = now - lastFrameTsRef.current;
        if (delta > JANK_MS && delta < MAX_GAP_MS) {
          const rounded = Math.round(delta);
          console.log(
            `[tbm][Frame] 프레임 지연 ${rounded}ms (메인 스레드 과부하 가능)`
          );
          try {
            localStorage.setItem(
              'tbm_last_frame_jank',
              JSON.stringify({
                time: new Date().toISOString(),
                deltaMs: rounded,
              })
            );
          } catch (_) {}
        }
      }
      lastFrameTsRef.current = now;
      frameWatchdogRafIdRef.current = requestAnimationFrame(tick);
    };
    frameWatchdogRafIdRef.current = requestAnimationFrame(tick);
  };

  const Init = async () => {
    // ── [LOG] 앱 전체 새로고침 vs React 리마운트 판별 ──────────────────────
    const mountNow = Date.now();
    const prevMount = sessionStorage.getItem('tbm_master_mount_time');
    if (prevMount) {
      const gapMs = mountNow - Number(prevMount);
      console.log(
        `[tbm][Master][Mount] 이전 마운트로부터 ${gapMs}ms 경과` +
          ` — ${gapMs < 2000 ? '⚠️ React 리마운트(빠른 재진입)' : 'React 리마운트'}`
      );
    } else {
      console.log(
        '[tbm][Master][Mount] 최초 마운트 또는 앱 전체 새로고침 후 진입'
      );
    }
    sessionStorage.setItem('tbm_master_mount_time', String(mountNow));
    // sessionStorage는 window.reload 시 초기화 → prevMount가 null이면 전체 새로고침
    // ──────────────────────────────────────────────────────────────────────

    const prevWsClose = localStorage.getItem('ws_last_close');
    const prevPdfLoad = localStorage.getItem('pdf_last_load');
    const prevLongTask = localStorage.getItem('tbm_last_longtask');
    const prevFrameJank = localStorage.getItem('tbm_last_frame_jank');
    if (prevWsClose) {
      const parsed = JSON.parse(prevWsClose);
      console.warn(
        `[tbm][이전 WS 종료 기록] code=${parsed.code} wasClean=${parsed.wasClean} reason="${parsed.reason}"` +
          ` time=${parsed.time}` +
          (parsed.code === 1006 ? ' ⚠️ 비정상 종료(네트워크 끊김 의심)' : '')
      );
    }
    if (prevPdfLoad) console.log('[tbm][이전 PDF 로드 기록]', prevPdfLoad);
    if (prevLongTask) console.log('[tbm][이전 LongTask 기록]', prevLongTask);
    if (prevFrameJank)
      console.log('[tbm][이전 Frame 지연 기록]', prevFrameJank);
    localStorage.removeItem('ws_last_close');
    localStorage.removeItem('pdf_last_load');
    localStorage.removeItem('tbm_last_longtask');
    localStorage.removeItem('tbm_last_frame_jank');

    // JS 메인 스레드 블로킹(Long Task) 감지 — 50ms 이상 JS가 막히면 로그
    if (typeof PerformanceObserver !== 'undefined') {
      try {
        longTaskObserverRef.current = new PerformanceObserver((list) => {
          list.getEntries().forEach((entry) => {
            const detail = {
              time: new Date().toISOString(),
              duration: Math.round(entry.duration),
            };
            console.log(`[tbm][LongTask] JS ${detail.duration}ms 블로킹 발생`);
            localStorage.setItem('tbm_last_longtask', JSON.stringify(detail));
          });
        });
        longTaskObserverRef.current.observe({ entryTypes: ['longtask'] });
      } catch (_) {}
    }

    dispatch(NavBarInfoActions.setType('titleBar'));
    dispatch(NavBarInfoActions.setTitleName('TBM'));
    dispatch(NavBarInfoActions.setBgColor('#F3F7F8'));

    await Search1Async({ sessionCd: participantData.sessionCd });
    await Search2Async({ sessionCd: participantData.sessionCd });
    await Search3Async({ sessionCd: participantData.sessionCd });

    OpenWebSocket();

    // 첫단계일때 qr모달 열기(진행자만)
    if (participantData.hostYn === 'Y' && sessionData.curStepOrder === 1) {
      dispatch(TbmActions.setQrModal(true));
    }
  };
  useEffect(() => {
    if (!participantData.sessionCd) return;
    Init();
    StartFrameWatchdog();

    const onVisibilityChange = () => {
      if (typeof document !== 'undefined' && !document.hidden) {
        lastFrameTsRef.current = null;
      }
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibilityChange);
    }

    let listener;
    if (Capacitor.isNativePlatform()) {
      listener = App.addListener('appStateChange', async (state) => {
        console.log(
          `[tbm][App] appStateChange — isActive=${state.isActive}, WS readyState=${ws.current ? ws.current.readyState : 'null'}`
        );
        // 앱이 포그라운드로 돌아옴
        if (state.isActive) {
          // 웹소켓 연결 상태 비정상
          if (!ws.current || ws.current.readyState !== WebSocket.OPEN) {
            console.log('[tbm][WS] 포그라운드 복귀 시 WS 비정상 — 재연결 시도');
            try {
              if (ws.current) ws.current.close();
              await OpenWebSocket();
            } catch (error) {
              dispatch(
                showSnackbar({
                  message:
                    '연결을 복구할 수 없습니다. 페이지를 새로고침해주세요.',
                  type: 'error',
                })
              );
            }
          } else {
            console.log('[tbm][WS] 포그라운드 복귀 — WS 정상');
          }
          // 포그라운드로 돌아왔을때 sessionData 조회 후 해당 스탭으로 이동
          Search1Async({ sessionCd: participantData.sessionCd });
        }
      });
    }

    return () => {
      if (ws.current) ws.current.close();
      StopHeartbeat();
      StopFrameWatchdog();
      if (longTaskObserverRef.current) {
        try {
          longTaskObserverRef.current.disconnect();
        } catch (_) {}
        longTaskObserverRef.current = null;
      }
      if (listener) listener.remove();
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibilityChange);
      }
    };
  }, []);

  useEffect(() => {
    if (sessionData.sessionCd) {
      // ── [LOG] sessionData 변경 감지 — 루프 발생 시 짧은 간격으로 반복 출력됨 ──
      console.log('[tbm][Master] sessionData 변경 → Save1 호출', {
        time: new Date().toISOString(),
        sessionCd: sessionData.sessionCd,
        curStepOrder: sessionData.curStepOrder,
        curStepCode: sessionData.curStepCode,
        curPosition: sessionData.curPosition,
        sessionStatus: sessionData.sessionStatus,
      });
      // ──────────────────────────────────────────────────────────────────────
      Save1([
        {
          ...stepData.find((f) => f.stepOrder === sessionData.curStepOrder),
          sessionCd: sessionData.sessionCd,
          stepOrder: sessionData.curStepOrder,
          stepCode: sessionData.curStepCode,
          lastPosition: sessionData.curPosition,
        },
      ]);
    }
  }, [sessionData]);

  // 모든 교육자료, 작업허가서 없이 교육을 시작한경우 추가 설정
  useEffect(() => {
    if (stepData.length === 3) {
      !IsHost && setModalToggle4(true);
      IsHost && dispatch(TbmActions.setMeetFlag(true));
    }
  }, [stepData.length, IsHost]);
  //#endregion

  //#region 쿼리
  const { runAsync: Search1Async } = useSearch1({
    onSuccess: (rows) => {
      const data = rows?.[0];

      // ── [LOG] Search1Async 응답 추적 — null이면 end 처리로 이어짐 ──────────
      console.log('[tbm][Master] Search1Async 응답', {
        time: new Date().toISOString(),
        hasData: !!data,
        sessionStatus: data?.sessionStatus,
        curStepOrder: data?.curStepOrder,
        curPosition: data?.curPosition,
        sessionCd: data?.sessionCd,
      });
      if (!data) {
        console.warn('[tbm][Master] ⚠️ Search1Async 결과 없음 → end 처리 진행');
      }
      // ──────────────────────────────────────────────────────────────────────

      dispatch(TbmActions.setSessionData(data));

      // data가 없는경우 종료
      if (!data) {
        ActiveButton({
          type: 'end',
          content: true,
        });
        return;
      } else {
        if (data.sessionStatus === 'OPEN') {
          // 진행중인 경우 진행중인 단계로 이동
          console.log(
            `[tbm][Master] sessionStatus=OPEN → setActiveStep(${data.curStepOrder - 1})`
          );
          dispatch(CommonActions.setActiveStep(data.curStepOrder - 1));
        } else if (data.sessionStatus === 'CLOSE') {
          // 완료된 경우 홈으로 이동
          console.warn('[tbm][Master] sessionStatus=CLOSE → navigate(/Home)');
          dispatch(
            showSnackbar({
              message: '완료된 TBM입니다. 홈으로 이동합니다.',
              type: 'warning',
            })
          );
          navigate('/Home');
        } else {
          // READY 상태인 경우 초기화
          console.log(
            `[tbm][Master] sessionStatus=${data.sessionStatus} → setActiveStep(0)`
          );
          dispatch(CommonActions.setActiveStep(0));
        }
      }
    },
  });
  const { runAsync: Search2Async } = useSearch2({
    onSuccess: (rows) => {
      dispatch(TbmActions.setStepData(rows));
    },
  });
  const { runAsync: Search3Async } = useSearch3({
    // qr모달 참가자 리스트 조회
    onSuccess: (rows) => {
      dispatch(TbmActions.setParticipants(rows));
    },
  });
  const { runAsync: Save1Async } = useSave1();
  const { runAsync: Delete1Async } = useDelete1();

  // 저장
  const { run: Save1 } = useSave({
    menuId: 'M_MASTER_BASIC',
    requestSave: (rows) => Save1Async(rows),
    showSuccess: false,
    onSuccess: async (rows) => {
      const data = rows.saveDetailData[0].data;
      dispatch(TbmActions.setStepData(data));

      // 웹소켓 전송
      ActiveButton({
        type: 'setStepData',
        stepData: data,
      });
    },
  });
  // 삭제
  const { run: Delete1 } = useDelete({
    menuId: 'M_Master_BASIC',
    requestDelete: (rows) => Delete1Async({ sessionCd: sessionData.sessionCd }),
    onSuccess: (rows) => {
      dispatch(CommonActions.setActiveStep(0));
    },
  });
  //#endregion

  //#region 나머지함수
  const OpenWebSocket = async () => {
    const sessionCd = participantData.sessionCd;
    if (!sessionCd) return;

    console.log(
      `[tbm][WS] OpenWebSocket 호출 — 기존 readyState=${ws.current ? ws.current.readyState : 'null'}`
    );

    if (ws.current) {
      try {
        ws.current.close();
      } catch (_) {
        /* noop */
      }
    }

    const roomId = encodeURIComponent(String(sessionCd));
    const userId = encodeURIComponent(Commons.DecryptoAES(userInfo.userid));
    const name = encodeURIComponent(userInfo.userNm ?? '');
    const entranceDt = encodeURIComponent(participantData.entranceDt);
    const wsUrl = `${process.env.REACT_APP_WEB_SOCKET}?roomId=${roomId}&userId=${userId}&name=${name}&entranceDt=${entranceDt}`;

    ws.current = new WebSocket(wsUrl);

    ws.current.onopen = () => {
      console.log('[tbm][WS] 연결됨');
      StartHeartbeat();
    };

    ws.current.onmessage = (event) => {
      const HandleText = (text) => {
        try {
          const data = JSON.parse(text);
          // ── [LOG] WS 수신 메시지 전체 추적 (pong 제외) ──────────────────────
          if (data.type !== 'pong') {
            console.log('[tbm][WS] 수신', {
              type: data.type,
              time: new Date().toISOString(),
              payload: JSON.stringify(data).slice(0, 300),
            });
          }
          // ──────────────────────────────────────────────────────────────────
          if (data.type === 'pong') {
            // heartbeat 응답 - 무시
          } else if (data.type === 'userCount') {
            // tbm 총 인원수
            dispatch(TbmActions.setUserCount(data.count));
            Search3Async({ sessionCd: participantData.sessionCd });
          } else if (data.type === 'message') {
            setMessages((prev) => [...prev, data.content]);
          } else if (data.type === 'setStepData') {
            dispatch(TbmActions.setStepData(data.stepData));
          } else if (data.type === 'stepChange') {
            console.log(`[tbm][WS] stepChange → setActiveStep(${data.step})`);
            if (participantData.hostYn !== 'Y') {
              const direction =
                data.step < activeStepRef.current ? 'prev' : 'next';
              dispatch(
                TbmActions.setStepTransition({
                  isTransitioning: true,
                  direction,
                })
              );
              setTimeout(() => {
                dispatch(
                  TbmActions.setStepTransition({
                    isTransitioning: false,
                    direction: 'next',
                  })
                );
                dispatch(CommonActions.setActiveStep(data.step));
              }, 1000);
            }
            // 진행자: startTransition 콜백에서 로컬 dispatch로 단계 이동 처리
          } else if (data.type === 'openMeetingDrawer') {
            participantData.hostYn === 'Y' &&
              dispatch(
                BottomSheetActions.OpenBottomSheet({ type: 'meetingDrawer' })
              );
          } else if (data.type === 'pageChange') {
            dispatch(TbmActions.setPageNumber(data.pageNumber));
          } else if (data.type === 'videoPlaying') {
            dispatch(TbmActions.setVideoPlaying(data.videoPlaying));
            dispatch(TbmActions.setVideoPlayedFraction(data.played));
          } else if (data.type === 'end') {
            if (data.waiting === 'Y') {
              participantData.hostYn !== 'Y' && setModalToggle4(true);
            } else if (data.waiting === 'N') {
              participantData.hostYn !== 'Y' && setModalToggle4(false);
            } else {
              console.warn('[tbm][WS] end(종료) 수신 → 3초 후 navigate(/Home)');
              if (ws.current) ws.current.close();
              participantData.hostYn !== 'Y' && setModalToggle3(true);
              setTimeout(() => {
                participantData.hostYn !== 'Y' && setModalToggle3(false);
                dispatch(CommonActions.setActiveStep(0));
                navigate('/Home');
              }, 500);
            }
          }
        } catch (e) {
          setMessages((prev) => [...prev, text]);
        }
      };

      if (event.data instanceof Blob) {
        const reader = new FileReader();
        reader.onload = () => HandleText(reader.result);
        reader.readAsText(event.data);
      } else {
        HandleText(event.data);
      }
    };

    ws.current.onerror = (error) => {
      console.error('[tbm][WS] 오류:', error);
    };

    ws.current.onclose = (event) => {
      StopHeartbeat();
      const detail = {
        time: new Date().toISOString(),
        code: event.code,
        wasClean: event.wasClean,
        reason: event.reason,
      };
      console.log('[tbm][WS] 종료:', JSON.stringify(detail));
      localStorage.setItem('ws_last_close', JSON.stringify(detail));

      const reason = (event.reason || '').trim();
      if (reason === 'kickParticipant') {
        dispatch(
          showSnackbar({
            message: '관리자에 의해 퇴장당했습니다.',
            type: 'warning',
          })
        );
        dispatch(CommonActions.setActiveStep(0));
        navigate('/Home');
      }
    };
  };

  const SendMessage = (title) => {
    if (ws.current && title.trim() !== '') {
      ws.current.send(title);
    }
  };

  const ActiveButton = useCallback((data) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(data));
    } else {
      const state = ws.current ? ws.current.readyState : 'null';
      console.log(
        `[tbm][WS] ActiveButton 전송 실패 — type=${data?.type}, readyState=${state}`
      );
    }
  }, []);

  const TbmEnd = () => {
    dispatch(CommonActions.setActiveStep(0));
    if (ws.current) ws.current.close();
    StopHeartbeat();
  };
  //#endregion

  return (
    <div className="wrap">
      <M_Master_BASIC
        modalToggle3={modalToggle3}
        setModalToggle3={setModalToggle3}
        modalToggle4={modalToggle4}
        setModalToggle4={setModalToggle4}
        Delete1={Delete1}
        ActiveButton={ActiveButton}
        SendMessage={SendMessage}
        TbmEnd={TbmEnd}
        meetFlag={meetFlag}
      />
    </div>
  );
}

export default M_Master_BASICF;
